/**
 * ResolutionHistoryModal - Displays paginated resolution history for a message thread.
 *
 * Fetches from /messages-v2/resolution-history with server-side pagination.
 * Pagination uses the same custom paginatorTemplate pattern as CaseListV2.
 */

import React, { useState, useEffect, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';

import constants from 'constants/index';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import { paginationDropdownOptions } from 'utils/utils';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { fetchResolutionHistory } from '../../services/messageService';

// ── Helpers ───────────────────────────────────────────────────────────────────

function statusSeverity(status) {
  switch (status) {
    case 'resolved':
      return 'success';
    case 'pending':
      return 'warning';
    default:
      return 'info';
  }
}

// ── Component ────────────────────────────────────────────────────────────────

function ResolutionHistoryModal({ visible, onHide, messageThreadId }) {
  const { userDetails } = useUserDetailsContext();
  const timezone = userDetails?.timezone;

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const loadPage = useCallback(
    (pageNum, size) => {
      if (!messageThreadId) return;
      setLoading(true);
      fetchResolutionHistory({ message_thread_id: messageThreadId, page: pageNum, page_size: size })
        .then((res) => {
          const body = res?.data?.body ?? res?.data ?? {};
          setItems(body.items || []);
          setTotalPages(body.pagination?.total_pages || 1);
        })
        .catch(() => {
          setItems([]);
          setTotalPages(1);
        })
        .finally(() => setLoading(false));
    },
    [messageThreadId]
  );

  useEffect(() => {
    if (visible) loadPage(page, pageSize);
  }, [visible, page, pageSize, loadPage]);

  const fetchNext = useCallback(() => {
    setPage((p) => p + 1);
  }, []);

  const fetchPrev = useCallback(() => {
    setPage((p) => p - 1);
  }, []);

  const onPageSizeChange = useCallback((e) => {
    setPageSize(e.value);
    setPage(1);
  }, []);

  const handleHide = useCallback(() => {
    setPage(1);
    setPageSize(10);
    setItems([]);
    setTotalPages(1);
    onHide();
  }, [onHide]);

  // ── Paginator template (matches CaseListV2 pattern) ───────────────────────

  const paginatorTemplate = {
    layout: 'RowsPerPageDropdown PrevPageLink NextPageLink',
    RowsPerPageDropdown: () => (
      <React.Fragment>
        <span className="mx-1" style={{ userSelect: 'none' }}>
          Items Per Page
        </span>
        <Dropdown value={pageSize} options={paginationDropdownOptions} onChange={onPageSizeChange} />
      </React.Fragment>
    ),
    PrevPageLink: ({ iconClassName }) => (
      <Button onClick={fetchPrev} disabled={page <= 1} className="p-paginator-prev p-paginator-element p-link" icon={iconClassName} />
    ),
    NextPageLink: ({ iconClassName }) => (
      <Button
        onClick={fetchNext}
        disabled={page >= totalPages}
        className="p-paginator-next p-paginator-element p-link"
        icon={iconClassName}
      />
    ),
  };

  // ── Column templates ───────────────────────────────────────────────────────

  const fmtDate = useCallback(
    (dateStr) => (dateStr ? convertDateFromApiToUserTimezone(dateStr, timezone, constants.month_date_year_time_12_format) || '—' : '—'),
    [timezone]
  );

  const requestedOnBody = (row) => fmtDate(row.requested_on);
  const requestedByBody = (row) => row.requested_by?.full_name || '—';
  const assignedUsersBody = (row) => {
    const names = (row.assigned_users || [])
      .map((u) => `${u.first_name || ''} ${u.last_name || ''}`.trim())
      .filter(Boolean)
      .join(', ');
    return names || '—';
  };
  const statusBody = (row) => <Tag value={row.status_display || row.status} severity={statusSeverity(row.status)} />;
  const resolvedByBody = (row) => row.resolved_by?.full_name || '—';
  const resolvedOnBody = (row) => fmtDate(row.resolved_on);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Dialog
      header="Resolution History"
      visible={visible}
      onHide={handleHide}
      style={{ width: '85vw', maxWidth: '1100px' }}
      className="common-popup-style"
      modal
      dismissableMask
    >
      <DataTable
        value={items}
        loading={loading}
        paginator
        paginatorTemplate={paginatorTemplate}
        rows={pageSize}
        emptyMessage="No resolution history found."
        className="p-datatable-sm"
        responsiveLayout="scroll"
        rowHover
      >
        <Column header="Requested On" body={requestedOnBody} style={{ minWidth: '160px' }} />
        <Column header="Requested By" body={requestedByBody} style={{ minWidth: '150px' }} />
        <Column header="Assigned Users" body={assignedUsersBody} style={{ minWidth: '180px' }} />
        <Column header="Current Status" body={statusBody} style={{ width: '130px' }} />
        <Column header="Resolved By" body={resolvedByBody} style={{ minWidth: '150px' }} />
        <Column header="Resolved On" body={resolvedOnBody} style={{ minWidth: '160px' }} />
      </DataTable>
    </Dialog>
  );
}

ResolutionHistoryModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  messageThreadId: PropTypes.string,
};

ResolutionHistoryModal.defaultProps = {
  messageThreadId: null,
};

export default memo(ResolutionHistoryModal);
